import { useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";

import { AccountOnHold } from "@/components/AccountOnHold";
import Box from "@/components/Box";
import { ExternalTextLink, InternalTextLink } from "@/components/Links";
import OrderIcon from "@/components/orders/OrderIcon";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { getAPIBase } from "@/core/config";
import { theme } from "@/core/theme";
import { ORGANISATION_STOCK_LIST_QUERY } from "@/graphql/organisations/queries/stock";
import { useDataLoader } from "@/hooks/useDataLoader";
import useListControls from "@/hooks/useListControls";
import useRefetch from "@/hooks/useRefetch";
import BackArrow from "@/images/BackArrow";
import Badge from "@/tpo/Badge";
import { PanelBoxV2 } from "@/tpo/Boxes";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import Group from "@/tpo/Group";
import { List, ListContext } from "@/tpo/List";
import Modal from "@/tpo/Modal";
import SimpleTable from "@/tpo/SimpleTable";
import SortMenu from "@/tpo/SortMenu";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import Table from "@/tpo/Table";
import ButtonV2, { ActionIcon } from "@/v2/Buttons";

import { REGENERUS_REGENCY } from "../constants";
import RegisterStock from "./ui/RegisterStock";
import Switcher from "./ui/Switcher";

function AccountOnHoldMessage({ withGreyOuterBox = true, maxWidth, mb }) {
  return (
    <AccountOnHold withGreyOuterBox={withGreyOuterBox} maxWidth={maxWidth} mb={mb}>
      <Box textAlign="center" mx="auto" fontSize={[14, 14, 16]}>
        Your account is currently paused due to an overdue invoice, please contact{" "}
        <ExternalTextLink href="mailto:info@regeneruslabs.com">
          info@regeneruslabs.com
        </ExternalTextLink>{" "}
        to proceed with registering stock
      </Box>
    </AccountOnHold>
  );
}

function StockDetails({
  children,
  product,
  createdFromOrder,
  registeredOrder,
  omnosBarcodeId,
  orderTestItem
}) {
  const { organisation } = useOrganisationContext();

  return (
    <Stack gap={5}>
      <Box fontFamily="gilroyBold" fontSize={16} data-testid="productName">
        {product.name}
      </Box>
      <InternalTextLink
        href={`/cmp/organisations/${organisation.hashId}/dashboard/order/${createdFromOrder.id}`}
      >
        Origin Stock Order: {createdFromOrder.id}
      </InternalTextLink>
      {!!registeredOrder && (
        <InternalTextLink
          href={`/cmp/organisations/${organisation.hashId}/dashboard/order/${registeredOrder.id}`}
        >
          Registered Order: {registeredOrder.id}
        </InternalTextLink>
      )}
      {product.providerApi !== REGENERUS_REGENCY ? (
        <Box fontFamily="gilroyMedium" fontSize={14} data-testid="KitRegistrationID">
          Kit registration ID: {orderTestItem?.providerTestId || omnosBarcodeId || "Awaiting code"}
        </Box>
      ) : (
        (orderTestItem?.providerTestId || omnosBarcodeId) && (
          <Box fontFamily="gilroyMedium" fontSize={14} data-testid="KitRegistrationID">
            Kit registration ID: {orderTestItem?.providerTestId || omnosBarcodeId}
          </Box>
        )
      )}
      {children}
    </Stack>
  );
}

function RegisterStockModal({ selectedStockId, setSelectedStockId }) {
  const history = useHistory();

  const [showCreatePatientForm, setShowCreatePatientForm] = useState(false);
  const [showEditPatientForm, setShowEditPatientForm] = useState(false);

  return (
    <Modal
      maxWidth={1020}
      closeButton
      headerProps={{
        p: [20, 20, 40]
      }}
      bg="white"
      show={!!selectedStockId}
      close={() => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete("stock");
        history.push({ search: urlParams.toString() });
        setSelectedStockId();
        setShowCreatePatientForm(false);
        setShowEditPatientForm(false);
      }}
      mode={["fullScreen", "fullScreen", "centered"]}
      data-component-name="RegisterStock"
      title={
        showCreatePatientForm || showEditPatientForm ? (
          <ActionIcon
            color="dark"
            height={[40, 40, 48]}
            width={[40, 40, 48]}
            borderRadius="50%"
            onClick={() => {
              setShowCreatePatientForm(false);
              setShowEditPatientForm(false);
            }}
            variant="outline"
            style={{
              pointerEvents: "auto"
            }}
            size={null}
          >
            <BackArrow color="black" />
          </ActionIcon>
        ) : null
      }
    >
      <PanelBoxV2
        maxWidth={760}
        outer={{
          bg: "white",
          px: [20, 20, 40],
          pb: theme.spacing.section.pb,
          borderRadius: 5
        }}
      >
        <RegisterStock
          stockId={selectedStockId}
          key={selectedStockId}
          showCreatePatientForm={showCreatePatientForm}
          setShowCreatePatientForm={setShowCreatePatientForm}
          showEditPatientForm={showEditPatientForm}
          setShowEditPatientForm={setShowEditPatientForm}
        />
      </PanelBoxV2>
    </Modal>
  );
}

export function OrganisationStock({ statusFilters = [] }) {
  const history = useHistory();

  const [filterTab, setFilterTab] = useState("all");

  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedStockId, setSelectedStockId] = useState();
  const [page, setPage] = useState(1);

  // use the url state to determine if we should preselect a stock id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stockId = urlParams.get("stock");
    if (stockId) {
      setSelectedStockId(stockId);
    }
  }, []);

  const controls = useListControls({
    defaultSort: "-created",
    sortOptions: [
      {
        label: "A - Z",
        value: "test_product__name"
      },
      {
        label: "Z - A",
        value: "-test_product__name"
      },
      {
        label: "Oldest To Newest",
        value: "created"
      },
      {
        label: "Newest To Oldest",
        value: "-created"
      }
    ]
  });

  const { organisation } = useOrganisationContext();
  const {
    data,
    loading,
    refetch: refetchStock
  } = useDataLoader(
    {
      query: ORGANISATION_STOCK_LIST_QUERY,
      variables: {
        page,
        organisation: parseInt(organisation?.id),
        orderBy: controls.sort,
        search: controls.debouncedSearch,
        filter: filterTab
      },
      cachePolicy: "no-cache",
      nextFetchPolicy: "no-cache",
      skip: !organisation
    },
    [organisation, page, controls.sort, controls.debouncedSearch, filterTab]
  );

  useRefetch(ORGANISATION_STOCK_LIST_QUERY, refetchStock);

  const orders = data?.organisationStockPagination?.items;
  const pageInfo = data?.organisationStockPagination?.pageInfo;

  const columns = useMemo(
    () => [
      {
        id: "kit",
        label: "Kit",
        accessor: ({
          id,
          omnosBarcodeId,
          testProduct: product,
          createdFromOrder: order,
          registeredOrder,
          orderTestItem
        }) => {
          return (
            <StockDetails
              omnosBarcodeId={omnosBarcodeId}
              product={product}
              createdFromOrder={order}
              registeredOrder={registeredOrder}
              orderTestItem={orderTestItem}
            />
          );
        }
      },
      {
        id: "orderDate",
        accessor: stock =>
          new Date(stock.createdFromOrder.checkoutDate).toLocaleDateString("en-GB"),
        label: "Ordered"
      },
      {
        id: "expires",
        accessor: stock => {
          // TODO Replace with real expiry calculations
          const date = new Date(stock.createdFromOrder.checkoutDate);
          date.setMonth(date.getMonth() + 3);
          return date.toLocaleDateString("en-GB");
        },
        label: "Expires"
      },
      {
        id: "lastColumn",
        accessor: ({
          id,
          registeredUserFullName,
          orderTestItem,
          testProduct,
          omnosBarcodeId,
          expiryDate
        }) => {
          expiryDate = expiryDate ? new Date(expiryDate) : null;

          if (registeredUserFullName) {
            return (
              <Stack gap={5}>
                <Badge bg="green" color="white" ml="auto" size="xs">
                  Registered
                </Badge>
                <Box fontFamily="gilroyBold" fontSize={16}>
                  {registeredUserFullName}
                </Box>
                {testProduct.providerApi === REGENERUS_REGENCY && (
                  <ExternalTextLink
                    href={`${getAPIBase()}/requisition/download/${orderTestItem.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ButtonV2
                      color="green"
                      rightIcon={<ChevronComponent />}
                      ml="auto"
                      mt={2}
                      size={["sm", "sm", "md"]}
                    >
                      Lab form
                    </ButtonV2>
                  </ExternalTextLink>
                )}
              </Stack>
            );
          } else {
            return (
              <ButtonV2
                color="dark"
                rightIcon={<ChevronComponent />}
                onClick={() => setSelectedStockId(id)}
                disabled={
                  organisation?.accountOnHold ||
                  (testProduct.providerApi !== REGENERUS_REGENCY &&
                    !(orderTestItem?.providerTestId || omnosBarcodeId)) ||
                  (expiryDate && expiryDate < new Date())
                }
                size={["sm", "sm", "md"]}
              >
                {expiryDate && expiryDate < new Date() ? "Expired" : "Register"}
              </ButtonV2>
            );
          }
        },
        align: "right"
      }
    ],
    [organisation]
  );

  const statusTabs = [
    {
      label: "all",
      slug: "all"
    },
    {
      label: "registered",
      slug: "registered"
    },
    {
      label: "not registered",
      slug: "not_registered"
    }
  ];

  return (
    <>
      <Stack
        maxWidth={1538}
        mx="auto"
        pt={theme.spacing.section.pt}
        pb={theme.spacing.section.pb}
        px={[20, 20, "5.5vw"]}
        gap={[20, 20, 40]}
      >
        <Center stacked>
          {organisation?.accountOnHold && (
            <AccountOnHoldMessage withGreyOuterBox={false} maxWidth={640} mb={60} />
          )}
          <Switcher />
        </Center>
        <Box
          bg="white"
          borderRadius={5}
          maxWidth={1280}
          pt={theme.spacing.section.pt}
          pb={theme.spacing.section.pb}
          px={[20, 20, 40]}
        >
          <Box maxWidth={1020} mx="auto">
            <Group gap={20}>
              <OrderIcon width={[30, 30, 40]} />
              <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                Organisation Stock
              </Box>
            </Group>
            <Spacer py={[2, 2, 20]} />
            <SimpleTable
              controls={
                <SortMenu
                  value={filterTab || null}
                  setValue={v => setFilterTab(v)}
                  open={showFilterMenu}
                  setOpen={setShowFilterMenu}
                  options={statusTabs.map(({ label, slug }) => ({
                    label: label.toUpperCase(),
                    value: slug
                  }))}
                  styles={{
                    button: {
                      color: "dark",
                      marginRight: "auto",
                      sx: {
                        color: "white"
                      }
                    }
                  }}
                  placeholder="Filter by status"
                />
              }
              search={controls.search}
              setSearch={controls.setSearch}
              sort={controls.sort}
              setSort={controls.setSort}
              sortMenuOpen={controls.sortMenuOpen}
              setSortMenuOpen={controls.setSortMenuOpen}
              pageInfo={pageInfo}
              setPage={setPage}
              sortingOptions={controls.sortOptions}
              table={
                <>
                  <Box display={["none", "none", "block"]}>
                    <Table columns={columns} loading={loading} rows={orders} />
                  </Box>
                  <Box display={["block", "block", "none"]}>
                    <List items={orders} loading={loading}>
                      <ListContext.Consumer>
                        {items =>
                          items?.map(item => {
                            return (
                              <Stack
                                key={item.id}
                                gap={20}
                                py={20}
                                style={{
                                  borderBottom: `1px solid ${theme.colors.midGrey}`
                                }}
                              >
                                <StockDetails
                                  omnosBarcodeId={item.omnosBarcodeId}
                                  product={item.testProduct}
                                  createdFromOrder={item.createdFromOrder}
                                  orderTestItem={item.orderTestItem}
                                  registeredOrder={item.registeredOrder}
                                />
                                <Group gap={10}>
                                  <Box fontFamily="gilroyBold" fontSize={16}>
                                    Order date:
                                  </Box>
                                  {columns[1].accessor(item)}
                                </Group>
                                <Group gap={10}>
                                  <Box fontFamily="gilroyBold" fontSize={16}>
                                    Expiry date:
                                  </Box>
                                  {columns[2].accessor(item)}
                                </Group>
                                <Group justifyContent="flex-start">
                                  {columns[3].accessor(item)}
                                </Group>
                              </Stack>
                            );
                          })
                        }
                      </ListContext.Consumer>
                    </List>
                  </Box>
                </>
              }
            />
          </Box>
        </Box>
      </Stack>
      <RegisterStockModal
        selectedStockId={selectedStockId}
        setSelectedStockId={setSelectedStockId}
      />
    </>
  );
}
